import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
	BlockOutlined,
	EditOutlined,
	SettingsBackupRestoreOutlined,
} from '@mui/icons-material';

import ContextMenu from 'src/components/common/context_menu/ContextMenu';
import { ConfirmationModal } from 'src/components/common/modal/ConfirmationModal';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	ISiteAccessTableActionsProps,
	siteAccessType,
	siteAccessStatus,
	siteAccessRole,
} from 'src/features/site_access/domain/types';
import {
	SITE_ACCESS_LOADING_KEY,
	updateSiteAccess,
} from 'src/features/site_access/store/siteAccessThunks';
import { AppDispatch } from 'src/store';

import { EditSiteAccessRoleModal } from './EditSiteAccessRoleModal';

export const SiteAccessTableActions: React.FC<ISiteAccessTableActionsProps> = ({
	siteSlug,
	selectedRow,
	anchorElement,
	clearAnchorElement,
	OnActionHit,
	onClose,
}) => {
	const dispatch = useDispatch<AppDispatch>();

	const [isSuspendModalVisible, setIsSuspendModalVisible] =
		useState<boolean>(false);
	const [isEditModalVisible, setIsEditModalVisible] =
		useState<boolean>(false);

	const menuVisible = Boolean(anchorElement);

	const handleUpdateSiteAccess = async (
		status: siteAccessStatus | undefined,
		type: siteAccessType | undefined,
		roles: siteAccessRole[] | undefined,
	) => {
		dispatch(
			updateSiteAccess({
				request: {
					...(status && { status }),
					...(roles && { roles }),
					...(type && { type }),
				},
				siteSlug: siteSlug,
				siteAccessId: selectedRow.id,
				onSuccess: () => {
					snackbar('Site access updated successfully', 'success');
					if (status) setIsSuspendModalVisible(false);
					if (roles || type) setIsEditModalVisible(false);
					OnActionHit();
				},
			}),
		);
	};

	return (
		<>
			<ContextMenu
				open={menuVisible}
				onClose={onClose}
				anchorElement={anchorElement}
				clearAnchorElement={clearAnchorElement}
				items={[
					{
						icon: <EditOutlined />,
						label: 'Edit type and roles',
						onClick: () => {
							setIsEditModalVisible(true);
						},
					},

					{
						icon:
							selectedRow.status === 'suspended' ? (
								<SettingsBackupRestoreOutlined />
							) : (
								<BlockOutlined />
							),

						label:
							selectedRow.status === 'suspended'
								? 'Activate access'
								: 'Suspend access',

						onClick: () => setIsSuspendModalVisible(true),
					},
				]}
			/>

			<EditSiteAccessRoleModal
				open={isEditModalVisible}
				defaultType={selectedRow.type}
				defaultRoles={selectedRow.roles}
				onClose={() => setIsEditModalVisible(false)}
				onSubmit={(data) => {
					handleUpdateSiteAccess(undefined, data.type, data.roles);
				}}
			/>

			<ConfirmationModal
				title={`Are you sure you want to ${
					selectedRow.status === 'suspended' ? 'activate' : 'suspend'
				} the access?`}
				modalContent={
					selectedRow.status === 'suspended'
						? 'The user will have their access to this site activated.'
						: "The user's access to this site will be suspended."
				}
				modalOpen={isSuspendModalVisible}
				handleYes={() =>
					handleUpdateSiteAccess(
						selectedRow.status === 'suspended'
							? 'active'
							: 'suspended',
						undefined,
						undefined,
					)
				}
				loadingKey={SITE_ACCESS_LOADING_KEY}
				handleNo={() => setIsSuspendModalVisible(false)}
				color={selectedRow.status === 'suspended' ? 'green' : undefined}
			/>
		</>
	);
};
